import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../../components/common/Button";
import Input from "../../../components/common/Input";
import AddressForm from "../../../components/common/AddressForm";
import CompanyDetailsForm from "../../../components/UI/CompanyDetailsForm";
import {
  getCompanyDetailsThunk,
  addCompanyDetailsThunk,
  updateCompanyDetailsThunk,
} from "../../../store/thunks/companyDetailsThunks";
import { OfficeBuildingIcon } from "@heroicons/react/solid";

const CompanyDetailsStep = ({ onComplete }) => {
  const dispatch = useDispatch();
  const { companyDetails, loading } = useSelector(
    (state) => state.companyDetails
  );
  const [showFullForm, setShowFullForm] = useState(false);
  const [formData, setFormData] = useState({
    companyName: "",
    website: "",
    taxId: "",
    registrationNumber: "",
    address: {},
    contactPerson: {
      name: "",
      email: "",
      phone: "",
    },
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    dispatch(getCompanyDetailsThunk());
  }, [dispatch]);

  useEffect(() => {
    if (companyDetails) {
      setFormData({
        companyName: companyDetails.companyName || "",
        website: companyDetails.website || "",
        taxId: companyDetails.taxId || "",
        registrationNumber: companyDetails.registrationNumber || "",
        address: companyDetails.address || {},
        contactPerson: companyDetails.contactPerson || {
          name: "",
          email: "",
          phone: "",
        },
      });
    }
  }, [companyDetails]);

  const handleChange = (field, value) => {
    if (field.includes(".")) {
      const [parent, child] = field.split(".");
      setFormData({
        ...formData,
        [parent]: {
          ...formData[parent],
          [child]: value,
        },
      });
    } else {
      setFormData({
        ...formData,
        [field]: value,
      });
    }

    // Clear error
    if (errors[field]) {
      setErrors({ ...errors, [field]: "" });
    }
  };

  const handleAddressChange = (addressData) => {
    setFormData({ ...formData, address: addressData });
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.companyName) {
      newErrors.companyName = "Company name is required";
    }
    if (!formData.contactPerson.email) {
      newErrors["contactPerson.email"] = "Contact email is required";
    }
    if (!formData.address.line1) {
      newErrors["address.line1"] = "Address is required";
    }
    if (!formData.address.city) {
      newErrors["address.city"] = "City is required";
    }
    if (!formData.address.state) {
      newErrors["address.state"] = "State is required";
    }
    if (!formData.address.pinCode) {
      newErrors["address.pinCode"] = "PIN code is required";
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSaving(true);
    try {
      const action = companyDetails?._id
        ? updateCompanyDetailsThunk
        : addCompanyDetailsThunk;

      await dispatch(action(formData)).unwrap();

      onComplete({
        companyDetailsCompleted: true,
        companyData: formData,
      });
    } catch (error) {
      setErrors({
        general: error || "Failed to save company details",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <span className="loader" aria-label="Loading..."></span>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h3 className="text-lg font-semibold mb-2">Company Information</h3>
        <p className="text-gray-600">
          Set up your company details. This information will be used across the
          system.
        </p>
      </div>

      {errors.general && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {errors.general}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-medium mb-4">Basic Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Company Name"
              value={formData.companyName}
              onChange={(e) => handleChange("companyName", e.target.value)}
              error={errors.companyName}
              placeholder="Enter company name"
              required
            />
            <Input
              label="Website"
              value={formData.website}
              onChange={(e) => handleChange("website", e.target.value)}
              placeholder="https://example.com"
            />
            <Input
              label="Tax ID (GSTIN)"
              value={formData.taxId}
              onChange={(e) => handleChange("taxId", e.target.value)}
              placeholder="15-character GSTIN"
              maxLength="15"
            />
            <Input
              label="Registration Number"
              value={formData.registrationNumber}
              onChange={(e) =>
                handleChange("registrationNumber", e.target.value)
              }
              placeholder="Company registration number"
            />
          </div>
        </div>

        {/* Address */}
        <div className="bg-gray-50 rounded-lg p-4">
          <AddressForm
            initialData={formData.address}
            onChange={handleAddressChange}
            errors={errors}
            showLabel={true}
          />
        </div>

        {/* Contact Person */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-medium mb-4">Contact Person</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Name"
              value={formData.contactPerson.name}
              onChange={(e) =>
                handleChange("contactPerson.name", e.target.value)
              }
              placeholder="Contact person name"
            />
            <Input
              label="Email"
              type="email"
              value={formData.contactPerson.email}
              onChange={(e) =>
                handleChange("contactPerson.email", e.target.value)
              }
              error={errors["contactPerson.email"]}
              placeholder="contact@example.com"
              required
            />
            <Input
              label="Phone"
              value={formData.contactPerson.phone}
              onChange={(e) =>
                handleChange("contactPerson.phone", e.target.value)
              }
              placeholder="10-digit mobile number"
              maxLength="10"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between">
          <Button
            type="button"
            variant="secondary-outline"
            onClick={() => setShowFullForm(true)}
          >
            Advanced Settings
          </Button>
          <Button
            type="submit"
            variant="primary"
            isLoading={saving}
            disabled={saving}
          >
            Save & Continue
          </Button>
        </div>
      </form>

      {/* Full Company Details Form Modal */}
      {showFullForm && (
        <CompanyDetailsForm
          isOpen={showFullForm}
          onClose={() => {
            setShowFullForm(false);
            dispatch(getCompanyDetailsThunk());
          }}
          initialData={formData}
        />
      )}
    </div>
  );
};

export default CompanyDetailsStep;
